// Shared modal/drawer overlay primitive — the canonical replacement for the
// hand-copied `<AppAbbrev>Overlay` + inline `theme: 'dialog'` Panel pattern
// forked across catalog/cms/commerce/crm/financials/helmet/hr/jobs/polyglot/
// scheduling/warehouse (see tickets/workspace.md "a11y: the catalog
// drawer/select/row pattern is duplicated..."). Every one of those forks was
// missing `role="dialog"`, an accessible name, and focus move-in/restore —
// this primitive owns all three plus keyboard dismissal, once, so a fix here
// reaches every consumer instead of recurring per app.
//
// Relationship to `@symbo.ls/windows` (ModalRoot/openModal/CustomTemplate,
// re-exported a few lines below in this same index): that plugin is the
// canonical IMPERATIVE, stack-based dialog host mounted once at the
// workspace SHELL root (`packages/workspace/app.js` → `ModalRoot: {}`) — the
// right tool for shell-level/cross-cutting modals (event/profile/booking)
// that call `el.call('openModal', {...})`. The 11 forked apps here are
// separately-published "workspace module" projects (own `symbols.json`,
// own manifest, injected into the shell's context at install time) whose
// existing modals are DECLARATIVE — a plain `hide: (el,s) => !s.root.flag`
// gate — not stack entries. Rewriting ~100 files across 11 independent
// projects onto the imperative stack API (state-carrier restructuring,
// `content:` closures, onConfirm/onClose rewiring) is a legitimately
// larger, separate migration; a design-meta follow-up ticket recommending
// it as the eventual destination was filed alongside this one. AppDialog
// gives every one of those files the SAME validated a11y contract
// (role, aria-modal, Tab-trap, focus-restore, Escape) TODAY via a
// mechanical, zero-state-model-change migration (swap the app's own
// `<AppAbbrev>Overlay` base for `AppDialog`) — not a competing dialog
// system, a narrower one scoped to exactly this consumption shape.
//
// ── v2: the floating overlay chassis (white-label redesign, 2026-09-26) ────
// DESIGN_SPEC §6 · IMPLEMENTATION_PLAN §2.2. One primitive, two presentations
// on a tablet or desktop, one on a phone:
//
//   AppDialog: {
//     stateKey: 'crmDealCreate',     // any root key; truthy = open (unchanged)
//     extraClose: { … },             // merged into the close write (unchanged)
//     presentation: 'center',        // 'center' (default) | 'side'
//                                    // legacy `anchor: 'right'` === 'side'
//     panelSize: 'md',               // 'sm' | 'md' | 'lg' | 'xl' → sizes
//                                    // overlaySm/Md/Lg/Xl (default: center md,
//                                    // side lg). NOT `size` — that name is a
//                                    // css-in-props shorthand (inline-size +
//                                    // block-size) and would paint on the root.
//     initialFocus: 'field',         // 'field' | 'panel' (default: center →
//                                    // field, side → panel)
//     dirty: (el, s) => boolean,     // true → a backdrop click is inert; Esc,
//                                    // Close and Cancel still close
//     closeCall: 'fnName',           // optional: a host-driven dialog names the
//                                    // function that closes it (called with the
//                                    // dialog element) instead of nulling
//                                    // stateKey — every close path uses it
//     Panel: {
//       AppDialogHead: { eyebrow, heading, titleId, subtitle },
//       AppDialogBody: { … },        // the ONLY scroll container
//       AppDialogFoot: { … }         // actions — never scrolls away
//     }
//   }
//
// A consumer WITHOUT `AppDialogBody` keeps the one scrolling Panel it always
// had (legacy mode: `B` inset, `A` gap, `overflow: auto`) — the ~160 existing
// call sites keep working while they migrate, and gain the float, the veil,
// the motion and the phone sheet for free.
//
// GEOMETRY. The root fills its frame (`absolute`, `inset: 0` — a workspace
// module mounts inside a transformed app window, which is the containing
// block of any `fixed` descendant; a `fixed` overlay would land at that
// container's scroll offset) and insets its panel by `overlayGutter`. A
// centre sheet sits in the middle; a side panel floats at the right edge,
// full height, all four corners rounded — nothing docks (P2). On a phone
// (`@mobileL`) every presentation becomes the bottom sheet: full width, the
// bottom edge, top corners only, a grabber. Alignment is flex on the root —
// the panel's `transform` is reserved for motion.
//
// MOTION (§6.4). Opacity and transform only; the veil fades, its blur never
// animates. Enter: `D` on `springBezier` (the PageSlot curve Nika approved);
// exit: `C` on `defaultBezier`. Centre rises 2.5% from 0.97, a side panel
// slides 4% from 0.98, the phone sheet rises 6.5%. The brand reset lands the
// final state at once under prefers-reduced-motion.
//
// HOW THE STATES REACH THE CSS. The root is ALWAYS mounted — `hide:` writes
// `display: none`, which kills both transitions. Open/closed and the
// presentation are published as DATA ATTRIBUTES (`data-open`,
// `data-presentation`) by reactive FLAT `dataOpen` / `dataPresentation`
// factories — never a `data: {}` block, which a consumer's own `data` object
// replaces wholesale on this runtime (FW-ELEMENT-DEFINITION-DATA-REPLACES-
// EXTENDS-DATA-1: the dialog would never open) — and every state rule is
// a STATIC selector block keyed on them (`'&[data-open]'`). Not factories:
// a factory-valued CSS prop is written INLINE, and an inline value outranks
// every `@mobileL` rule, so the phone sheet could never take over. Not `.isX`
// blocks: those apply at create only on this runtime. The phone pose rides a
// custom property (`--app-dialog-from`) so the open rule's `transform: none`
// and the phone rule never fight over one property. The framework's cascade
// tiers put these compound rules after the flat classes and the `@mobileL`
// rules after both, so the order holds whichever element minted a rule first.
//
// Closed = `inert` + `visibility: hidden` + `pointer-events: none` on the
// root, the visibility after the exit (a `C` delay on its transition; none
// on open): not focusable, out of the accessibility tree, and click-through.
// The root clips in both states, so a parked panel adds no scroll size.
//
// Dismissal: Esc (bubbles from the focused descendant to the root, which
// stops it there — only the top-most overlay closes), a backdrop click
// (inert while `dirty`), the head's Close, a footer Cancel — all four through
// the dialog's own `scope.closeNearest` (below). Tab is trapped in the Panel (appDialogTrapTab —
// NOT `inert`, which would silence the backdrop's own dismiss handlers);
// focus moves in on open (appDialogFocusPanel, honouring `initialFocus`) and
// returns to the opener on close (appDialogSync).
export const AppDialog = {
  // THE close path, carried by the dialog itself (frank-safe `scope`, the
  // Modal.js pattern): every descendant reaches it as `el.scope.closeNearest`
  // through the scope chain, so Esc, the veil, the head's Close and a footer
  // Cancel close the dialog in ANY function registry — a standalone module's
  // own sharedLibraries.js registers only the four 2026-08 functions.
  // `appDialogCloseNearest` (functions/) is the registered entry for code
  // outside these handlers, and delegates here.
  //
  //   from   the element that asked (the root, the veil, any Panel descendant)
  //   opts   { fromBackdrop: true } for the veil — inert while `dirty`
  //
  // Walks up to the nearest dialog root (the element that owns a Backdrop
  // and a Panel). A host-driven dialog names its own close function in
  // `closeCall`; everything else nulls `stateKey` (+ `extraClose`) on the
  // root state. Returns true when a close was requested.
  scope: {
    closeNearest(from, opts) {
      let dialog = from
      while (dialog && !(dialog.Backdrop && dialog.Panel)) dialog = dialog.parent
      if (!dialog) return false
      if (opts && opts.fromBackdrop && dialog.dirty) return false
      if (typeof dialog.closeCall === 'string' && dialog.closeCall) {
        dialog.call(dialog.closeCall, dialog)
        return true
      }
      const key = dialog.stateKey
      const root = dialog.state && dialog.state.root
      if (!key || !root || typeof root.update !== 'function') return false
      const extra =
        dialog.extraClose &&
        typeof dialog.extraClose === 'object' &&
        !Array.isArray(dialog.extraClose)
          ? dialog.extraClose
          : null
      root.update(extra ? { [key]: null, ...extra } : { [key]: null })
      return true
    }
  },

  // '' = not chosen: a legacy `anchor: 'right'` then means 'side', anything
  // else 'center'. An explicit presentation always wins over `anchor`.
  presentation: '',
  panelSize: '',
  initialFocus: '',
  dirty: false,

  isOpen: (el, s) => {
    const open = !!s.root?.[el.stateKey]
    el.call('appDialogSync', open)
    return open
  },

  // The two state hooks every rule below keys on. Reading `el.isOpen` here is
  // also what keeps `appDialogSync` (focus in / focus back) firing on every
  // open and close — `isOpen`'s side effect only runs when an effect reads it.
  dataOpen: (el) => !!el.isOpen,
  dataPresentation: (el) =>
    el.presentation === 'side' || (!el.presentation && el.anchor === 'right') ? 'side' : 'center',
  // Nothing in a closed dialog is focusable or read. `visibility: hidden`
  // (below) hides the closed root, but a descendant that sets its own
  // `visibility: visible` (an inline error whose draft outlives the dialog)
  // would still take focus and be read (BESSI QA 2026-10-28). The attribute
  // leaves in the update that opens the dialog; focus moves in one frame
  // later (appDialogSync).
  inert: (el) => (el.isOpen ? null : ''),

  position: 'absolute',
  // THE FRAME. `--app-dialog-inset` is the band the shell leaves free on a
  // full-page mount (below its floating header, the sheet's own margins);
  // unset = 0, the frame itself (a PageSlot pane, a module standalone). A
  // full-page mount is a scroller with no position, so the root resolves
  // against the viewport and an `inset: 0` put the side sheet under the
  // header (persona audit 2026-10-25: Bookmarks, Translations editors).
  inset: 'var(--app-dialog-inset, 0)',
  zIndex: '600',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  padding: 'overlayGutter',
  visibility: 'hidden',
  pointerEvents: 'none',
  // The dialog adds no scroll size, closed or open. A closed panel keeps its
  // parked transform (`--app-dialog-from`, below) and an opening one starts
  // from it: both reach past this frame, and the scroller around it took a
  // sideways scroll from them (BESSI QA 2026-10-28, 5px on dev
  // kestrel-assist /production — the route's `content`, then the document).
  // `clip`, not `hidden`: the root stays no scroll container. The open
  // panel's shadow ends at the frame edge, where the veil ends too.
  overflow: 'clip',
  transition: 'visibility 0s linear C',

  '&[data-presentation="side"]': {
    alignItems: 'stretch',
    justifyContent: 'flex-end'
  },
  '&[data-open]': {
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'visibility 0s linear 0s'
  },
  // The phone sheet touches the frame: no gutter.
  '@mobileL': { padding: '0' },

  // Escape closes from anywhere inside the dialog — keydown bubbles from
  // whichever descendant holds focus up to this root — and STOPS here, so an
  // enclosing surface (a PageSlot window, a dialog underneath) is untouched.
  onKeydown: (e, el) => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    el.scope.closeNearest(el)
  },

  Backdrop: {
    position: 'absolute',
    inset: '0',
    theme: 'backdrop',
    opacity: '0',
    transition: 'opacity B defaultBezier',
    dataOpen: (el) => !!el.parent.isOpen,
    '&[data-open]': {
      opacity: '1',
      transition: 'opacity C defaultBezier'
    },
    onClick: (e, el) => el.scope.closeNearest(el, { fromBackdrop: true })
  },

  Panel: {
    position: 'relative',
    display: 'flex',
    flow: 'y',
    boxSizing: 'border-box',
    minWidth: '0',
    minHeight: '0',
    // The size token of the presentation; a consumer's own static `width`
    // replaces this whole prop at merge and keeps winning.
    width: (el) => {
      const p = el.parent || {}
      const side = p.presentation === 'side' || (!p.presentation && p.anchor === 'right')
      const size = p.panelSize || (side ? 'lg' : 'md')
      return (
        { sm: 'overlaySm', md: 'overlayMd', lg: 'overlayLg', xl: 'overlayXl' }[size] || 'overlayMd'
      )
    },
    maxWidth: '100%',
    maxHeight: '100%',
    // The sheet: opaque `dialog` (white / gray3) on the `overlay` elevation,
    // `radiusSheet` on all four corners in both presentations. Body copy keeps
    // the `paragraph` ink every consumer was written against.
    theme: 'dialog',
    color: 'paragraph',
    round: 'radiusSheet',
    shadow: 'overlay',

    // LEGACY vs ANATOMY. With the three anatomy slots the panel itself is a
    // non-scrolling column and AppDialogBody scrolls; without them the panel
    // is the one scroller it always was.
    padding: (el) => (el.AppDialogBody ? '0' : 'B'),
    gap: (el) => (el.AppDialogBody ? '0' : 'A'),
    overflow: (el) => (el.AppDialogBody ? 'hidden' : 'auto'),
    overscrollBehavior: 'contain',

    // Motion: the closed pose is a custom property per presentation (and per
    // viewport, below); the open rule resets transform to none.
    '--app-dialog-from': 'translate3d(0, 2.5%, 0) scale(0.97)',
    transform: 'var(--app-dialog-from)',
    transformOrigin: 'center',
    opacity: '0',
    transition: 'opacity C defaultBezier, transform C defaultBezier',
    dataOpen: (el) => !!el.parent.isOpen,
    dataPresentation: (el) => {
      const p = el.parent
      return p.presentation === 'side' || (!p.presentation && p.anchor === 'right')
        ? 'side'
        : 'center'
    },
    '&[data-presentation="side"]': {
      '--app-dialog-from': 'translate3d(4%, 0, 0) scale(0.98)',
      transformOrigin: 'right center'
    },
    '&[data-open]': {
      opacity: '1',
      transform: 'none',
      transition: 'opacity D springBezier, transform D springBezier'
    },

    // THE PHONE SHEET. `minWidth: 100%` outranks any width (a consumer's own
    // included) without a specificity fight; `alignSelf` pins it to the
    // bottom whatever the root's alignment; the bottom corners square off.
    '@mobileL': {
      minWidth: '100%',
      maxHeight: '92%',
      alignSelf: 'flex-end',
      borderBottomLeftRadius: '0',
      borderBottomRightRadius: '0',
      // THE HOME INDICATOR (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1). The sheet
      // meets the screen's bottom edge, and on an iPhone without a home
      // button the last 34pt of it are the system's swipe zone: the foot's
      // Cancel / Save sat in it (an `A` inset, ~16px, from the edge). A
      // TRANSPARENT bottom border the size of the inset: the sheet's own
      // surface paints under it (background-clip is the border box), so the
      // sheet still reaches the edge, while the content — the padding box,
      // and in a legacy panel the scrollport — ends above the zone. One rule
      // for the anatomy and the legacy panel alike; 0 wherever the device
      // reports no inset (viewport-fit=cover is set in index.html).
      borderBottomWidth: 'env(safe-area-inset-bottom)',
      borderBottomStyle: 'solid',
      borderBottomColor: 'transparent',
      '&[data-presentation]': {
        '--app-dialog-from': 'translate3d(0, 6.5%, 0)',
        transformOrigin: 'bottom center'
      }
    },

    // Dialog semantics — role/aria-modal always on. The accessible name is
    // the head's title when an AppDialogHead is present; a consumer that
    // points `ariaLabelledby` at its own title id replaces this.
    role: 'dialog',
    ariaModal: 'true',
    tabindex: '-1',
    // The panel takes focus itself (`initialFocus: 'panel'`, every side
    // panel by default). The brand reset's [tabindex]:focus-visible ring is a
    // box-shadow and would REPLACE the sheet's elevation after a keyboard
    // open (found by crm-w1 on the contact panel) — the sheet keeps it.
    ':focus-visible': { boxShadow: 'var(--shadow-overlay)' },
    ariaLabelledby: (el) => (el.AppDialogHead && el.AppDialogHead.titleId) || null,
    ariaDescribedby: (el) => {
      const head = el.AppDialogHead
      return head && head.titleId && head.subtitle ? head.titleId + 'Hint' : null
    },

    onKeydown: (e, el) => el.call('appDialogTrapTab', e),

    // The phone sheet's grabber — decoration (the sheet closes by Esc, the
    // backdrop and Close; a swipe is a later addition). Absent on wider
    // screens. `line` ink: a `layer` wash (4%) reads 1.08:1 on the white
    // sheet, the hairline tone reads.
    SheetGrabber: {
      ariaHidden: 'true',
      display: 'none',
      position: 'absolute',
      top: 'Y',
      left: '50%',
      width: 'grabberW',
      height: 'grabberH',
      round: 'radiusPill',
      background: 'line',
      transform: 'translateX(-50%)',
      pointerEvents: 'none',
      '@mobileL': { display: 'block' }
    }
  }
}

// ── The dialog's anatomy ────────────────────────────────────────────────────
// Declared ONCE, so no dialog hand-builds its head or footer.
//
// v2 (2026-09-26) — three slots that make the panel a fixed head, ONE
// scrolling body and actions that never scroll away:
//
//   Panel: {
//     AppDialogHead: {
//       eyebrow: 'crm.deal.eyebrow',   // mono micro-label (optional)
//       heading: 'crm.deal.new',       // a bundle key or a plain string
//       titleId: 'crmDealCreateTitle', // names the dialog (aria-labelledby)
//       subtitle: 'crm.deal.hint'      // optional; describes the dialog
//     },
//     AppDialogBody: { …fields… },
//     AppDialogFoot: { AppCapsuleBtnGhost: {…}, AppCapsuleBtnPrimary: {…} }
//   }
//
// `heading`, not `title`: `title` is a global HTML attribute — it would paint a
// native tooltip on the head, and a `{{ … | polyglot }}` template on it
// resolves to '' in the static attribute pass (docs/I18N-CONVENTION.md §1.2,
// the EmptyState rename). The three texts go through polyglot at render, so a
// call site passes a KEY or a plain string (polyglot returns a non-key
// verbatim); a function value is read like any factory.
//
// 2026-09-24 parts, kept: AppDialogTitle / AppDialogHint (the head's title
// and subtitle ARE these two) and AppDialogFooter (the equal-width Cancel +
// primary pair, now built on AppDialogFoot).
//
// The subtitle and the helper text are `inactive` — never a lighter grey (the
// reference's own subtitles measure 3.79–3.85:1; `inactive` on the panel
// reads 7.38 light / 6.44 dark).
export const AppDialogTitle = {
  tag: 'h2',
  margin: '0',
  fontSize: 'B',
  fontWeight: '700',
  color: 'title'
}

export const AppDialogHint = {
  tag: 'p',
  margin: '0',
  fontSize: 'Z1',
  color: 'inactive'
}

// THE HEAD — the brand ModalHeader (title group left, the filled Close right)
// with the dialog's own texts. Close runs the dialog's close path, whatever
// opened it (the brand default calls the windows plugin's `closeModal`).
export const AppDialogHead = {
  extends: 'ModalHeader',
  eyebrow: '',
  heading: '',
  titleId: '',
  subtitle: '',
  flexShrink: '0',
  padding: 'B B A',
  '@mobileL': { padding: 'B A A' },

  // The brand header's own H (an h6) and P are replaced, not restyled: the
  // three texts are the house parts by name.
  Hgroup: {
    flow: 'y',
    gap: 'X',
    minWidth: '0',
    H: null,
    P: null,
    AppMicroLabel: {
      order: '-1',
      hide: (el) => !el.parent.parent.eyebrow,
      text: (el) => el.call('polyglot', el.parent.parent.eyebrow || '')
    },
    AppDialogTitle: {
      lineHeight: '1.25',
      id: (el) => el.parent.parent.titleId || null,
      text: (el) => el.call('polyglot', el.parent.parent.heading || '')
    },
    AppDialogHint: {
      lineHeight: '1.5',
      id: (el) => {
        const head = el.parent.parent
        return head.titleId && head.subtitle ? head.titleId + 'Hint' : null
      },
      hide: (el) => !el.parent.parent.subtitle,
      text: (el) => el.call('polyglot', el.parent.parent.subtitle || '')
    }
  },

  // THE CLOSE BUTTON'S TOUCH AREA STAYS INSIDE THE PANEL
  // (DESIGN-APPDIALOG-CLOSE-SLOP-PASSES-PANEL-EDGE-1 — the same defect class
  // Modal.js already carries for ModalWindow: ModalHeader's Icons group pulls
  // in by Z1, and on a coarse pointer the brand IconButton's 6px tap slop
  // (`:after`, inset tapSlop) then passes the head's own `A` right padding by
  // 2px at every phone width (measured dev `7e8916816668c`: 377of375, 395of393,
  // 442of440). Pulling the group in by `Y` instead on `@coarse` keeps the slop
  // inside the padding; a mouse keeps ModalHeader's own Z1 alignment.
  Icons: {
    '@coarse': { marginRight: '-Y' },
    Close: {
      onClick: (e, el) => {
        e.stopPropagation()
        if (el.scope && el.scope.closeNearest) el.scope.closeNearest(el)
      }
    }
  }
}

// THE BODY — the one scroll container. Contain its overscroll so a flick at
// the end never scrolls the page behind the veil.
export const AppDialogBody = {
  flow: 'y',
  gap: 'A',
  flex: '1 1 auto',
  minHeight: '0',
  overflow: 'auto',
  overscrollBehavior: 'contain',
  padding: '0 B B',
  '@mobileL': { padding: '0 A A' }
}

// THE FOOT — the actions, after the body in the column, so a long form never
// scrolls them away. Right-aligned, `Z` apart; on a phone every action takes
// an equal share of the width (thumb reach).
//
// THE OVERFLOW LAW (HOME_AMBIENT_SKIN_SPEC §3b, DESIGN-WL-D2-KIT-OVERFLOW-LAW-1):
// the foot never pushes an action out of the sheet. A row that does not fit
// WRAPS (it overflowed to the left and the sheet's radius clip cut it — three
// actions of 415 px in a 460 px module window,
// DESIGN-APPDIALOGFOOT-LONG-ACTIONS-CLIP-AND-OVERFLOW-1); on a phone two
// actions keep their equal thumb-reach columns and three or more STACK, one
// per row, so no label is squeezed into a third of 390 px.
export const AppDialogFoot = {
  display: 'flex',
  // ONE `flex-flow: row wrap` declaration — a separate `flexWrap` would be a
  // second atomic class racing the `flow` shorthand's implied `nowrap`.
  flow: 'x wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 'Z',
  flexShrink: '0',
  padding: '0 B B',
  '@mobileL': {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(0, 1fr)',
    padding: '0 A A',
    '&:has(> :nth-child(3))': { gridAutoFlow: 'row' }
  }
}

// The equal-width pair (2026-09-24): the brand tonal CancelButton (closes the
// dialog by default) and its ink CheckButton (the primary). EQUAL columns
// whatever each button's own padding — a `flex: 1 1 0` share measured 268.5px
// against 159.5px live (the brand CancelButton's wider padding stays outside
// the share); grid tracks of minmax(0, 1fr) measured 214px / 214px.
export const AppDialogFooter = {
  extends: 'AppDialogFoot',
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: 'minmax(0, 1fr)',
  justifyContent: 'stretch',
  // Each label WRAPS inside its equal column (the overflow law's text rule):
  // the brand Button atom is `nowrap`, so a Georgian "Cancel" in half of a
  // 390 px sheet spilled past its capsule.
  //
  // THE PHONE PAIR: the brand capsules own their geometry (Rule 68). Brand
  // company 6eea987 puts CancelButton and CheckButton on the house capsule
  // scale (padding `0 A`); in half of a 390 px sheet that leaves about 127 px
  // of label room, so the pair needs no phone override here. (A compact
  // override lived here for the pre-6eea987 brand, whose `Z1 D1` Cancel left
  // a ~4 px content box; it was removed before any dev build carried it.)
  childProps: {
    minWidth: '0',
    minHeight: 'controlH',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere'
  },
  CancelButton: {
    tag: 'button',
    type: 'button',
    text: '{{ common.action.cancel | polyglot }}',
    onClick: (e, el) => {
      if (el.scope && el.scope.closeNearest) el.scope.closeNearest(el)
    }
  },
  CheckButton: {
    Icon: null,
    type: 'button',
    text: '{{ common.action.confirm | polyglot }}'
  }
}
